import React from 'react';
import { Container, Typography, Box, Paper, Divider } from '@mui/material';
import SEO from '../components/SEO';

const TermsOfServicePage = () => {
  return (
    <>
      <SEO
        title="Terms of Service - Couplendar"
        description="Read Couplendar's terms of service to understand your rights and responsibilities when using our relationship balance tracking app."
        url="https://couplendar.com/terms-of-service"
      />
      <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Terms of Service
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ '& > *': { mb: 3 } }}>
          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              Welcome to Couplendar. By accessing or using our application, website, and services (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms constitute a legally binding agreement between you and Couplendar ("we," "our," or "us"). By creating an account or using the Service, you represent that you have read, understood, and agree to these Terms.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              2. Description of Service
            </Typography>
            <Typography variant="body1" paragraph>
              Couplendar is an application designed to help couples track shared activities, chores, and time-off to promote equality and balance in relationships. The Service allows users to:
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li>Create and manage user accounts</li>
                <li>Connect with a partner to form a relationship within the application</li>
                <li>Track daily chores and time-off activities</li>
                <li>View statistics and insights about relationship activities</li>
                <li>Access the Service through web and mobile applications</li>
              </ul>
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              3. Eligibility
            </Typography>
            <Typography variant="body1" paragraph>
              You must be at least 16 years of age to use our Service. By using the Service, you represent and warrant that you are at least 16 years old and have the legal capacity to enter into these Terms.
            </Typography>
            <Typography variant="body1" paragraph>
              If you are using the Service on behalf of an organization or entity, you represent and warrant that you have the authority to bind that organization to these Terms.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              4. User Accounts
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              4.1 Account Creation
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li>You must provide accurate, current, and complete information during registration</li>
                <li>You must verify your email address before accessing the full features of the Service</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You must notify us immediately of any unauthorized use of your account</li>
                <li>You may only create one account per person</li>
              </ul>
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              4.2 Account Security
            </Typography>
            <Typography variant="body1" paragraph>
              You are solely responsible for all activities that occur under your account. You agree to use a strong password and to keep your login credentials secure. We are not liable for any loss or damage arising from your failure to maintain account security.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              4.3 Account Deletion
            </Typography>
            <Typography variant="body1" paragraph>
              You may delete your account at any time through the account settings. Upon deletion, your personal data will be permanently removed from our systems within 30 days, subject to legal retention requirements. This action is irreversible.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              5. Acceptable Use
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li>Violate any applicable local, national, or international law or regulation</li>
                <li>Infringe upon the rights of others, including intellectual property rights</li>
                <li>Transmit any malicious code, viruses, or harmful content</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Use automated systems (bots, scrapers, etc.) to access the Service without permission</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
                <li>Use the Service for commercial purposes without our prior written consent</li>
                <li>Collect or store personal data about other users without their consent</li>
              </ul>
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              6. User Content
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              6.1 Your Responsibilities
            </Typography>
            <Typography variant="body1" paragraph>
              You retain ownership of any content you submit to the Service ("User Content"), including chore entries, time-off records, and feedback. You are solely responsible for your User Content and the consequences of sharing it through the Service.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              6.2 License to Use Your Content
            </Typography>
            <Typography variant="body1" paragraph>
              By submitting User Content to the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, store, display, and process your User Content solely for the purpose of providing and improving the Service. This license terminates when you delete your content or account.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              6.3 Shared Content
            </Typography>
            <Typography variant="body1" paragraph>
              When you connect with a partner in the application, certain User Content will be shared with your partner as part of the Service's functionality. By using the Service, you consent to this sharing of relationship-related data with your connected partner.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              7. Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph>
              The Service, including its design, features, functionality, text, graphics, logos, and software, is owned by Couplendar and is protected by copyright, trademark, and other intellectual property laws.
            </Typography>
            <Typography variant="body1" paragraph>
              You may not copy, modify, distribute, sell, or lease any part of our Service without our prior written permission. You are granted a limited, non-exclusive, non-transferable license to access and use the Service for personal, non-commercial purposes.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              8. Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your personal information. By using the Service, you consent to our collection and use of information as described in our Privacy Policy.
            </Typography>
            <Typography variant="body1" paragraph>
              Please review our Privacy Policy at <a href="/privacy-policy" style={{ color: '#9c27b0' }}>/privacy-policy</a> to understand our practices.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              9. Disclaimers and Limitations of Liability
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              9.1 Service Provided "As Is"
            </Typography>
            <Typography variant="body1" paragraph>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              9.2 Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, COUPLENDAR SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              9.3 No Professional Advice
            </Typography>
            <Typography variant="body1" paragraph>
              The Service is designed to help couples track activities and promote relationship balance. It is not intended to provide professional relationship counseling, therapy, or medical advice. Always seek the advice of qualified professionals for relationship or mental health concerns.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              10. Termination
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to suspend or terminate your access to the Service at any time, with or without notice, for any reason, including but not limited to:
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li>Violation of these Terms</li>
                <li>Fraudulent or illegal activity</li>
                <li>Requests by law enforcement or government agencies</li>
                <li>Discontinuation or material modification of the Service</li>
              </ul>
            </Typography>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              Upon termination, your right to use the Service will immediately cease. You may also terminate your account at any time by deleting it through the account settings.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              11. Indemnification
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to indemnify, defend, and hold harmless Couplendar, its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with:
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li>Your access to or use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Your User Content</li>
              </ul>
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              12. Changes to the Service
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.
            </Typography>
            <Typography variant="body1" paragraph>
              We may also impose limits on certain features or restrict access to parts of the Service without notice or liability.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              13. Changes to These Terms
            </Typography>
            <Typography variant="body1" paragraph>
              We may update these Terms from time to time. When we make material changes, we will notify you by:
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li>Updating the "Last Updated" date at the top of these Terms</li>
                <li>Sending you an email notification</li>
                <li>Displaying a prominent notice within the Service</li>
              </ul>
            </Typography>
            <Typography variant="body1" paragraph>
              Your continued use of the Service after the changes become effective constitutes your acceptance of the updated Terms. If you do not agree to the updated Terms, you must stop using the Service.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              14. Governing Law and Dispute Resolution
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Couplendar operates, without regard to its conflict of law provisions.
            </Typography>
            <Typography variant="body1" paragraph>
              Any disputes arising out of or relating to these Terms or the Service shall be resolved through good faith negotiations. If negotiations fail, disputes will be resolved through binding arbitration or in the courts of competent jurisdiction.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              15. Severability
            </Typography>
            <Typography variant="body1" paragraph>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect. The invalid or unenforceable provision will be replaced with a valid provision that most closely matches the intent of the original provision.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              16. Entire Agreement
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms, together with our Privacy Policy and any other policies referenced herein, constitute the entire agreement between you and Couplendar regarding the Service and supersede all prior agreements and understandings.
            </Typography>
          </section>

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              17. Contact Information
            </Typography>
            <Typography variant="body1" component="div">
              If you have any questions, concerns, or feedback regarding these Terms of Service, please contact us:
              <Box sx={{ mt: 2, ml: 2 }}>
                <Typography variant="body1">
                  <strong>Email:</strong> support@couplendar.com
                </Typography>
              </Box>
            </Typography>
          </section>

          <Divider sx={{ my: 3 }} />

          <section>
            <Typography variant="body2" color="text.secondary" paragraph>
              By using Couplendar, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </Typography>
          </section>
        </Box>
      </Paper>
    </Container>
    </>
  );
};

export default TermsOfServicePage;
